/**
 * Google Analytics Integration for PDF Statement Converter
 * Provides comprehensive event tracking and enhanced ecommerce analytics
 */

import { getAnalytics, logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import app from '../firebase/config';
import Logger from './logger';

// Analytics instance
let analytics = null;
let isInitialized = false;

// Development mode flag
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Initialize Firebase Analytics
 * @returns {Object} Analytics instance or null
 */
export const initializeAnalytics = () => {
  try {
    if (typeof window === 'undefined') {
      Logger.analytics('Server-side environment detected, skipping initialization');
      return null;
    }

    analytics = getAnalytics(app);
    isInitialized = true;
    
    Logger.analytics('Analytics initialized successfully', {
      measurementId: 'G-QJ1W5BK8YW',
      environment: process.env.NODE_ENV,
      debugMode: isDevelopment
    });
    
    return analytics;
  } catch (error) {
    Logger.error('Analytics initialization failed:', error);
    return null;
  }
};

/**
 * Safe analytics logging with error handling
 * @param {string} eventName - Event name
 * @param {Object} parameters - Event parameters
 */
const safeLogEvent = (eventName, parameters = {}) => {
  try {
    if (!isInitialized || !analytics) {
      Logger.warn('Analytics not initialized, skipping event:', eventName, parameters);
      return;
    }

    logEvent(analytics, eventName, parameters);
    
    Logger.analytics(`Event: ${eventName}`, parameters);
  } catch (error) {
    Logger.error('Analytics event logging failed:', error);
  }
};

/**
 * Set user properties for enhanced analytics
 * @param {Object} properties - User properties
 */
export const setAnalyticsUserProperties = (properties) => {
  try {
    if (!isInitialized || !analytics) return;
    
    setUserProperties(analytics, properties);
    
    Logger.analytics('User Properties Set:', properties);
  } catch (error) {
    Logger.error('Setting user properties failed:', error);
  }
};

/**
 * Set user ID for analytics
 * @param {string} userId - User ID
 */
export const setAnalyticsUserId = (userId) => {
  try {
    if (!isInitialized || !analytics) return;
    
    setUserId(analytics, userId);
    
    Logger.analytics('User ID Set:', userId);
  } catch (error) {
    Logger.error('Setting user ID failed:', error);
  }
};

// =============================================================================
// PAGE TRACKING
// =============================================================================

/**
 * Track page view events
 * @param {string} pageTitle - Page title
 * @param {string} pageLocation - Page URL/path
 * @param {Object} additionalParams - Additional parameters
 */
export const trackPageView = (pageTitle, pageLocation, additionalParams = {}) => {
  safeLogEvent('page_view', {
    page_title: pageTitle,
    page_location: pageLocation,
    ...additionalParams
  });
};

/**
 * Track app session start
 * @param {Object} sessionInfo - Session information
 */
export const trackSessionStart = (sessionInfo = {}) => {
  safeLogEvent('session_start', {
    timestamp: new Date().toISOString(),
    ...sessionInfo
  });
};

// =============================================================================
// USER REGISTRATION & AUTHENTICATION
// =============================================================================

/**
 * Track user registration events
 * @param {string} method - Registration method (email, google)
 * @param {string} userId - User ID
 * @param {Object} additionalData - Additional registration data
 */
export const trackUserRegistration = (method, userId, additionalData = {}) => {
  safeLogEvent('sign_up', {
    method: method,
    user_id: userId,
    timestamp: new Date().toISOString(),
    ...additionalData
  });
  
  // Set user properties for new user
  setAnalyticsUserProperties({
    registration_method: method,
    registration_date: new Date().toISOString(),
    user_type: 'free_user'
  });
  
  setAnalyticsUserId(userId);
};

/**
 * Track user login events
 * @param {string} method - Login method
 * @param {string} userId - User ID
 */
export const trackUserLogin = (method, userId) => {
  safeLogEvent('login', {
    method: method,
    user_id: userId,
    timestamp: new Date().toISOString()
  });
  
  setAnalyticsUserId(userId);
};

/**
 * Track registration form abandonment
 * @param {string} lastField - Last field user interacted with
 * @param {number} timeSpent - Time spent on form (seconds)
 */
export const trackRegistrationAbandonment = (lastField, timeSpent) => {
  safeLogEvent('registration_abandonment', {
    last_field: lastField,
    time_spent_seconds: timeSpent,
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// PDF UPLOAD & CONVERSION TRACKING
// =============================================================================

/**
 * Track PDF upload start
 * @param {Object} fileInfo - File information
 */
export const trackPDFUploadStarted = (fileInfo) => {
  const { fileName, fileSize, fileType, creditsRequired } = fileInfo;
  
  safeLogEvent('pdf_upload_started', {
    file_name: fileName,
    file_size_bytes: fileSize,
    file_type: fileType,
    credits_required: creditsRequired,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track PDF upload confirmation (when user confirms credit usage)
 * @param {Object} confirmationData - Confirmation data
 */
export const trackPDFUploadConfirmed = (confirmationData) => {
  const { fileName, creditsUsed, userCreditsRemaining } = confirmationData;
  
  safeLogEvent('pdf_upload_confirmed', {
    file_name: fileName,
    credits_used: creditsUsed,
    user_credits_remaining: userCreditsRemaining,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track PDF conversion success
 * @param {Object} conversionData - Conversion data
 */
export const trackPDFConversionSuccess = (conversionData) => {
  const { 
    fileName, 
    creditsUsed, 
    processingTimeSeconds, 
    outputFormat,
    transactionId 
  } = conversionData;
  
  safeLogEvent('pdf_conversion_success', {
    file_name: fileName,
    credits_used: creditsUsed,
    processing_time_seconds: processingTimeSeconds,
    output_format: outputFormat,
    transaction_id: transactionId,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track PDF conversion error
 * @param {Object} errorData - Error data
 */
export const trackPDFConversionError = (errorData) => {
  const { fileName, errorType, errorMessage, creditsRefunded } = errorData;
  
  safeLogEvent('pdf_conversion_error', {
    file_name: fileName,
    error_type: errorType,
    error_message: errorMessage,
    credits_refunded: creditsRefunded || 0,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track upload abandonment
 * @param {string} stage - Stage where user abandoned (file_selected, analyzing, confirming)
 * @param {Object} fileInfo - File information if available
 */
export const trackUploadAbandonment = (stage, fileInfo = {}) => {
  safeLogEvent('upload_abandonment', {
    abandonment_stage: stage,
    file_name: fileInfo.fileName || 'unknown',
    credits_required: fileInfo.creditsRequired || 0,
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// ENHANCED ECOMMERCE - CREDIT PURCHASES
// =============================================================================

/**
 * Track when user views credit purchase options
 * @param {Object} viewData - View data
 */
export const trackCreditPurchaseView = (viewData = {}) => {
  safeLogEvent('view_item_list', {
    item_list_id: 'credit_packages',
    item_list_name: 'Credit Purchase Options',
    items: [
      {
        item_id: 'variable_credits',
        item_name: 'Variable Credit Purchase',
        item_category: 'credits',
        item_variant: 'variable_pricing'
      }
    ],
    ...viewData
  });
};

/**
 * Track credit selection (add to cart equivalent)
 * @param {Object} selectionData - Selection data
 */
export const trackCreditSelection = (selectionData) => {
  const { quantity, pricePerCredit, totalPrice, pricingTier } = selectionData;
  
  safeLogEvent('add_to_cart', {
    currency: 'ZAR',
    value: totalPrice,
    items: [{
      item_id: `credits_${quantity}`,
      item_name: `${quantity} Credits`,
      item_category: 'credits',
      item_variant: pricingTier,
      quantity: quantity,
      price: pricePerCredit
    }]
  });
};

/**
 * Track beginning of checkout process
 * @param {Object} checkoutData - Checkout data
 */
export const trackCreditCheckoutBegin = (checkoutData) => {
  const { quantity, totalPrice, pricingTier } = checkoutData;
  
  safeLogEvent('begin_checkout', {
    currency: 'ZAR',
    value: totalPrice,
    coupon: checkoutData.promoCode || '',
    items: [{
      item_id: `credits_${quantity}`,
      item_name: `${quantity} Credits`,
      item_category: 'credits',
      item_variant: pricingTier,
      quantity: quantity,
      price: checkoutData.pricePerCredit
    }]
  });
};

/**
 * Track successful credit purchase
 * @param {Object} purchaseData - Purchase data
 */
export const trackCreditPurchase = (purchaseData) => {
  const { 
    transactionId, 
    quantity, 
    totalPrice, 
    pricingTier, 
    pricePerCredit,
    paymentMethod,
    savings,
    userId 
  } = purchaseData;
  
  // Enhanced ecommerce purchase event
  safeLogEvent('purchase', {
    transaction_id: transactionId,
    currency: 'ZAR',
    value: totalPrice,
    tax: 0,
    shipping: 0,
    coupon: purchaseData.promoCode || '',
    items: [{
      item_id: `credits_${quantity}`,
      item_name: `${quantity} Credits`,
      item_category: 'credits',
      item_variant: pricingTier,
      quantity: quantity,
      price: pricePerCredit
    }]
  });
  
  // Custom credit purchase event
  safeLogEvent('credit_purchase', {
    transaction_id: transactionId,
    credits_purchased: quantity,
    amount_zar: totalPrice,
    price_per_credit: pricePerCredit,
    pricing_tier: pricingTier,
    payment_method: paymentMethod,
    savings_amount: savings || 0,
    user_id: userId,
    timestamp: new Date().toISOString()
  });
  
  // Update user properties
  setAnalyticsUserProperties({
    user_type: 'paying_customer',
    last_purchase_date: new Date().toISOString(),
    total_credits_purchased: quantity // This should be cumulative in real implementation
  });
};

/**
 * Track credit purchase abandonment
 * @param {string} stage - Stage where abandoned (selection, payment_form, payment_processing)
 * @param {Object} abandonmentData - Abandonment data
 */
export const trackCreditPurchaseAbandonment = (stage, abandonmentData = {}) => {
  safeLogEvent('credit_purchase_abandonment', {
    abandonment_stage: stage,
    selected_credits: abandonmentData.quantity || 0,
    selected_amount: abandonmentData.totalPrice || 0,
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// FILE DOWNLOAD TRACKING
// =============================================================================

/**
 * Track file download events
 * @param {Object} downloadData - Download data
 */
export const trackFileDownload = (downloadData) => {
  const { fileName, fileType, transactionId, fileSizeBytes, userId } = downloadData;
  
  safeLogEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    file_size_bytes: fileSizeBytes,
    transaction_id: transactionId,
    user_id: userId,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track download errors
 * @param {Object} errorData - Error data
 */
export const trackDownloadError = (errorData) => {
  const { fileName, errorType, errorMessage, transactionId } = errorData;
  
  safeLogEvent('download_error', {
    file_name: fileName,
    error_type: errorType,
    error_message: errorMessage,
    transaction_id: transactionId,
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// PROMO CODE TRACKING
// =============================================================================

/**
 * Track promo code usage
 * @param {Object} promoData - Promo code data
 */
export const trackPromoCodeUsage = (promoData) => {
  const { promoCode, discountAmount, discountType, success, errorMessage } = promoData;
  
  safeLogEvent('promo_code_usage', {
    promo_code: promoCode,
    discount_amount: discountAmount || 0,
    discount_type: discountType || 'unknown',
    success: success,
    error_message: errorMessage || '',
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// ERROR TRACKING
// =============================================================================

/**
 * Track application errors
 * @param {Object} errorData - Error data
 */
export const trackError = (errorData) => {
  const { errorType, errorMessage, component, userId, additionalContext } = errorData;
  
  safeLogEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage,
    component: component,
    user_id: userId || 'anonymous',
    timestamp: new Date().toISOString(),
    ...additionalContext
  });
};

/**
 * Track payment errors specifically
 * @param {Object} paymentErrorData - Payment error data
 */
export const trackPaymentError = (paymentErrorData) => {
  const { errorType, errorMessage, amount, paymentMethod, transactionId } = paymentErrorData;
  
  safeLogEvent('payment_error', {
    error_type: errorType,
    error_message: errorMessage,
    amount_zar: amount || 0,
    payment_method: paymentMethod || 'unknown',
    transaction_id: transactionId || '',
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// USER ENGAGEMENT TRACKING
// =============================================================================

/**
 * Track feature usage
 * @param {string} featureName - Feature name
 * @param {Object} usageData - Usage data
 */
export const trackFeatureUsage = (featureName, usageData = {}) => {
  safeLogEvent('feature_usage', {
    feature_name: featureName,
    timestamp: new Date().toISOString(),
    ...usageData
  });
};

/**
 * Track user engagement events
 * @param {string} engagementType - Type of engagement
 * @param {Object} engagementData - Engagement data
 */
export const trackUserEngagement = (engagementType, engagementData = {}) => {
  safeLogEvent('user_engagement', {
    engagement_type: engagementType,
    timestamp: new Date().toISOString(),
    ...engagementData
  });
};

/**
 * Track dashboard view events
 * @param {Object} dashboardData - Dashboard view context data
 */
export const trackDashboardView = (dashboardData = {}) => {
  safeLogEvent('dashboard_view', {
    page_title: 'Dashboard',
    timestamp: new Date().toISOString(),
    ...dashboardData
  });
};

/**
 * Track file download started events  
 * @param {Object} downloadData - Download started data
 */
export const trackFileDownloadStarted = (downloadData = {}) => {
  safeLogEvent('file_download_started', {
    timestamp: new Date().toISOString(),
    ...downloadData
  });
};

/**
 * Track file download completed events
 * @param {Object} downloadData - Download completed data
 */
export const trackFileDownloadCompleted = (downloadData = {}) => {
  safeLogEvent('file_download_completed', {
    timestamp: new Date().toISOString(),
    ...downloadData
  });
};

/**
 * Track file download error events
 * @param {Object} errorData - Download error data
 */
export const trackFileDownloadError = (errorData = {}) => {
  safeLogEvent('file_download_error', {
    timestamp: new Date().toISOString(),
    ...errorData
  });
};

/**
 * Track help/support interactions
 * @param {string} helpType - Type of help requested
 * @param {Object} helpData - Help interaction data
 */
export const trackHelpInteraction = (helpType, helpData = {}) => {
  safeLogEvent('help_interaction', {
    help_type: helpType,
    timestamp: new Date().toISOString(),
    ...helpData
  });
};

// =============================================================================
// PERFORMANCE TRACKING
// =============================================================================

/**
 * Track performance metrics
 * @param {Object} performanceData - Performance data
 */
export const trackPerformance = (performanceData) => {
  const { metricName, value, unit } = performanceData;
  
  safeLogEvent('performance_metric', {
    metric_name: metricName,
    metric_value: value,
    metric_unit: unit,
    timestamp: new Date().toISOString()
  });
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if analytics is initialized and ready
 * @returns {boolean} Analytics ready status
 */
export const isAnalyticsReady = () => {
  return isInitialized && analytics !== null;
};

/**
 * Get analytics instance (for advanced usage)
 * @returns {Object|null} Analytics instance
 */
export const getAnalyticsInstance = () => {
  return analytics;
};

/**
 * Enable/disable debug mode
 * @param {boolean} enabled - Debug mode enabled
 */
export const setAnalyticsDebugMode = (enabled) => {
  Logger.analytics(`Debug mode ${enabled ? 'enabled' : 'disabled'}`);
};

// Initialize analytics on module load
initializeAnalytics();